import { useContext, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { SelectListener } from './ui'
import { ISelectListener, ISelectListenerContainer } from './types'

import { useListeners } from '@api/listeners/state'

import { CardModeType } from '@smart/common.type'
import { CardListenerContainer, ICardListenerContainer } from '@smart/cardListener'

import { useValue } from '@hooks/useValue'
import { useDebounce } from '@hooks/useDebounce'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


type ListenersSnapshot = Pick<ReturnType<typeof useListeners.getState>, 'error' | 'isReloadListeners' | 'listeners'>


/** Выбор слушателя - Контейнер */
export const SelectListenerContainer = ({isOpen, handlerButtonOk, handlerButtonCancel}: ISelectListenerContainer) => {
    const notify = useContext(NotificationContext)

    const { error, isReloadListeners, isLoading, totalCount, pageSize, listenersList, getListeners, clearListeners } = useListeners(useShallow((state) => ({
        error: state.error,
        isReloadListeners: state.isReloadListeners,
        
        isLoading: state.listeners.isLoading,
        totalCount: state.listeners.totalCount,
        pageSize: state.listeners.pageSize,

        listenersList: state.listeners.items,
        getListeners: state.actionGetListeners,
        clearListeners: state.clearListenersState,
    })))

    const cardListeners = useValue<ICardListenerContainer>({isOpen: false})

    const currentPageNumber = useValue<number>(1)
    const txtSearch = useValue<string>('')
    const listenersSnapshotRef = useRef<ListenersSnapshot | null>(null)


    useEffect(() => {
        if (!isOpen) return

        const currentState = useListeners.getState()
        listenersSnapshotRef.current = {
            error: currentState.error,
            isReloadListeners: currentState.isReloadListeners,
            listeners: {
                ...currentState.listeners,
                items: [...currentState.listeners.items],
            },
        }
        loadData()

        return (() => {
            const snapshot = listenersSnapshotRef.current
            if (snapshot) {
                clearListeners()
                useListeners.setState(snapshot)
                listenersSnapshotRef.current = null
            }

            txtSearch.setValue('')
            currentPageNumber.setValue(1)
        })
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => { if (error) return notify && notifyOpen(error?.message, error?.type, 3000, notify) }, [error]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { isOpen && loadData(txtSearch.value) }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { isReloadListeners && loadData(txtSearch.value) }, [isReloadListeners]) // eslint-disable-line react-hooks/exhaustive-deps


    /** Функция по получению списка слушателей */
    const loadData = (search?: string) => {
        getListeners(currentPageNumber.value, search || '')
    }


    /** Открытие/закрытие карточки по слушателю */
    const handleOpenCardListener = (isOpen: boolean, cardMode: CardModeType, cardID?: number) => {
        cardListeners.setValue({
            isOpen, cardMode, cardID,
            handlerButtonCancel() {cardListeners.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardListeners.setValue({isOpen: false})
                loadData(txtSearch.value)
            }
        })
    }

    const debounceSearch = useDebounce(loadData, 1500) // Запуск загрузки заявок через полторы секунды
    const txtSearchOnChange = (value: string) => {txtSearch.setValue(value); debounceSearch(value)} // Поле поиска    

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ISelectListener = {
        isOpen, handlerButtonOk, handlerButtonCancel,

        isLoading, totalCount, pageSize, listenersList, 
        txtSearch, txtSearchOnChange, currentPageNumber,
        handleOpenCardListener,
    }    

    return <>
        <SelectListener {...propsToComponent} />
        <CardListenerContainer {...cardListeners.value} />
    </>
}